import { fetchJSON as fetchAPI } from './client';

export type RegisteredRepository = {
  id: string;
  tenant_id?: string;
  name: string;
  service: string;
  aggregate: string;
  kind: string;
  storage_type: string;
  isolation_mode: string;
  supports_cqrs: boolean;
  supports_soft_delete: boolean;
  supports_audit: boolean;
  supports_pagination: boolean;
  schema?: string;
  table_name?: string;
  product_family?: string;
  registered_at: string;
};

export type MigrationRecord = {
  id: string;
  tenant_id?: string;
  service: string;
  version: string;
  name: string;
  status: string;
  checksum: string;
  applied_at?: string;
  duration_ms?: number;
  tenant_aware: boolean;
};

export type TransactionLogEntry = {
  id: string;
  tenant_id: string;
  correlation_id?: string;
  trace_id?: string;
  service: string;
  status: string;
  operations: number;
  retry_count: number;
  started_at: string;
  completed_at?: string;
};

export type ArchiveRecord = {
  id: string;
  tenant_id: string;
  aggregate: string;
  entity_id: string;
  archived_at: string;
  retention_days: number;
  legal_hold: boolean;
};

export type RetentionPolicy = {
  id: string;
  name: string;
  aggregate: string;
  retain_days: number;
  archive_days: number;
  purge_after_days: number;
  enabled: boolean;
};

export type CQRSModel = {
  id: string;
  name: string;
  service: string;
  model_type: string;
  projection?: string;
  materialized: boolean;
  event_driven: boolean;
  registered_at: string;
};

export type AuditMetadata = {
  entity_type: string;
  entity_id: string;
  created_by: string;
  created_at: string;
  modified_by?: string;
  modified_at?: string;
  deleted_by?: string;
  deleted_at?: string;
  tenant_id: string;
  correlation_id?: string;
  trace_id?: string;
  change_reason?: string;
};

export type PersistenceHealth = {
  tenant_id: string;
  persistence_health_score: number;
  migration_success_rate: number;
  transaction_success_rate: number;
  rollback_frequency: number;
  avg_query_latency_ms: number;
  storage_growth_gb: number;
  archive_utilization_pct: number;
  registered_repositories: number;
  active_migrations: number;
  pending_migrations: number;
  validated_at: string;
};

export type ExtensionPoint = {
  dev_id: string;
  name: string;
  description: string;
};

export type PersistenceDashboard = {
  tenant_id: string;
  health: PersistenceHealth;
  repositories: number;
  migrations: number;
  transactions: number;
  archives: number;
  cqrs_models: number;
  retention_policies: number;
  extension_points: ExtensionPoint[];
  generated_at: string;
};

export type RunMigrationRequest = {
  service?: string;
  version?: string;
  dry_run?: boolean;
};

export type RunMigrationResult = {
  applied: number;
  skipped: number;
  migrations: string[];
  message: string;
};

const PERSISTENCE_BASE = import.meta.env.VITE_PERSISTENCE_API ?? '/api/v1/persistence';

function fetchPersistence<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${PERSISTENCE_BASE}${path}`, init);
}

export const persistenceGovernanceApi = {
  listRepositories: () =>
    fetchPersistence<{ repositories: RegisteredRepository[] }>('/repositories').then((r) => r.repositories),

  listMigrations: () =>
    fetchPersistence<{ migrations: MigrationRecord[] }>('/migrations').then((r) => r.migrations),

  runMigration: (body: RunMigrationRequest) =>
    fetchPersistence<{ data: RunMigrationResult }>('/migrations/run', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.data),

  listTransactions: () =>
    fetchPersistence<{ transactions: TransactionLogEntry[] }>('/transactions').then((r) => r.transactions),

  listArchives: () =>
    fetchPersistence<{ archives: ArchiveRecord[] }>('/archive').then((r) => r.archives),

  health: () => fetchPersistence<PersistenceHealth>('/health'),

  dashboard: () => fetchPersistence<PersistenceDashboard>('/dashboard'),

  listRetention: () =>
    fetchPersistence<{ retention_policies: RetentionPolicy[] }>('/retention').then((r) => r.retention_policies),

  listCQRS: () =>
    fetchPersistence<{ cqrs_models: CQRSModel[] }>('/cqrs').then((r) => r.cqrs_models),

  listAudit: () =>
    fetchPersistence<{ audit: AuditMetadata[] }>('/audit').then((r) => r.audit),

  extensionPoints: () =>
    fetchPersistence<{ extension_points: ExtensionPoint[] }>('/extensions').then((r) => r.extension_points),
};
